import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import {
  ArrowLeft,
  ArrowDownToLine,
  ArrowUpFromLine,
  Check,
  CheckCircle2,
  Loader2,
  RotateCcw,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Avatar } from '../components/ui/Avatar';
import { Toast } from '../components/ui/Toast';

interface Props {
  user: User;
}

type TradeStatus =
  | 'matching'
  | 'selecting'
  | 'pending_confirm'
  | 'confirmed'
  | 'cancelled'
  | 'undone';

interface TradeRow {
  id: string;
  user_a: string;
  user_b: string;
  status: TradeStatus;
  selections_a: string[];
  selections_b: string[];
  confirmed_a: boolean;
  confirmed_b: boolean;
  cancelled_at: string | null;
  cancelled_by: string | null;
  confirmed_at: string | null;
  created_at: string;
  expires_at: string;
}

interface PartnerInfo {
  id: string;
  display_name: string;
  username: string | null;
}

interface StickerCount {
  sticker_code: string;
  count: number;
}

interface WishlistEntry {
  sticker_code: string;
}

const POLL_MS = 2000;
const UNDO_MS = 5 * 60 * 1000;

function asJsonbArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string') as string[];
  return [];
}

function normalizeTrade(raw: any): TradeRow {
  return {
    id: raw.id,
    user_a: raw.user_a,
    user_b: raw.user_b,
    status: raw.status,
    selections_a: asJsonbArray(raw.selections_a),
    selections_b: asJsonbArray(raw.selections_b),
    confirmed_a: !!raw.confirmed_a,
    confirmed_b: !!raw.confirmed_b,
    cancelled_at: raw.cancelled_at,
    cancelled_by: raw.cancelled_by,
    confirmed_at: raw.confirmed_at,
    created_at: raw.created_at,
    expires_at: raw.expires_at,
  };
}

export default function TradePage({ user }: Props) {
  const { tradeId } = useParams<{ tradeId: string }>();
  const navigate = useNavigate();
  const [trade, setTrade] = useState<TradeRow | null>(null);
  const [partner, setPartner] = useState<PartnerInfo | null>(null);
  const [myCounts, setMyCounts] = useState<Record<string, number>>({});
  const [partnerCounts, setPartnerCounts] = useState<Record<string, number>>({});
  const [myWishlist, setMyWishlist] = useState<Set<string>>(new Set());
  const [partnerWishlist, setPartnerWishlist] = useState<Set<string>>(new Set());
  const [mySelection, setMySelection] = useState<string[]>([]);
  const [partnerSelection, setPartnerSelection] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmedLocal, setConfirmedLocal] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const initialLoadRef = useRef(true);

  const isUserA = trade ? trade.user_a === user.id : false;
  const mySide = trade ? (isUserA ? 'a' : 'b') : null;
  const isExpired =
    trade !== null &&
    ['matching', 'selecting', 'pending_confirm'].includes(trade.status) &&
    new Date(trade.expires_at).getTime() < now;

  // Tick para atualizar o countdown.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Polling do trade.
  useEffect(() => {
    if (!tradeId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      const { data, error: qErr } = await supabase
        .from('trades')
        .select(
          'id, user_a, user_b, status, selections_a, selections_b, confirmed_a, confirmed_b, cancelled_at, cancelled_by, confirmed_at, created_at, expires_at'
        )
        .eq('id', tradeId)
        .maybeSingle();

      if (cancelled) return;

      if (qErr) {
        setReconnecting(true);
      } else {
        setReconnecting(false);
        if (!data) {
          setError('Troca não encontrada.');
          return;
        }
        const next = normalizeTrade(data);
        if (next.user_a !== user.id && next.user_b !== user.id) {
          setError('Você não faz parte dessa troca.');
          return;
        }
        setTrade(next);
        if (initialLoadRef.current) {
          initialLoadRef.current = false;
          // Inicializa seleção local com o que já está salvo.
          const myInitial = next.user_a === user.id ? next.selections_a : next.selections_b;
          setMySelection(myInitial);
          setPartnerSelection(
            next.user_a === user.id ? next.selections_b : next.selections_a
          );
        } else {
          // Mantém minha seleção local em sincronia com o que o servidor refletir.
          const myFromServer =
            next.user_a === user.id ? next.selections_a : next.selections_b;
          const partnerFromServer =
            next.user_a === user.id ? next.selections_b : next.selections_a;
          if (next.status === 'pending_confirm' || next.status === 'confirmed') {
            setMySelection(myFromServer);
          }
          setPartnerSelection(partnerFromServer);
        }
      }
      if (!cancelled) timer = setTimeout(poll, POLL_MS);
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [tradeId, user.id]);

  // Carrega partner + counts dos dois + wishlists assim que tradeId chega.
  useEffect(() => {
    if (!trade) return;
    let cancelled = false;
    const partnerId = trade.user_a === user.id ? trade.user_b : trade.user_a;
    (async () => {
      const [profileRes, myStickersRes, partnerStickersRes, myWlRes, partnerWlRes] =
        await Promise.all([
          supabase
            .from('profiles_public')
            .select('user_id, display_name, username')
            .eq('user_id', partnerId)
            .maybeSingle(),
          supabase
            .from('stickers')
            .select('sticker_code, count')
            .eq('user_id', user.id),
          supabase
            .from('stickers')
            .select('sticker_code, count')
            .eq('user_id', partnerId),
          supabase.from('wishlist').select('sticker_code').eq('user_id', user.id),
          supabase.from('wishlist').select('sticker_code').eq('user_id', partnerId),
        ]);

      if (cancelled) return;

      const prof = profileRes.data as
        | { user_id: string; display_name: string; username: string | null }
        | null;
      setPartner({
        id: partnerId,
        display_name: prof?.display_name ?? 'Parceiro',
        username: prof?.username ?? null,
      });

      const myMap: Record<string, number> = {};
      for (const row of (myStickersRes.data ?? []) as StickerCount[]) {
        myMap[row.sticker_code] = row.count;
      }
      setMyCounts(myMap);

      const partnerMap: Record<string, number> = {};
      for (const row of (partnerStickersRes.data ?? []) as StickerCount[]) {
        partnerMap[row.sticker_code] = row.count;
      }
      setPartnerCounts(partnerMap);

      setMyWishlist(
        new Set(((myWlRes.data ?? []) as WishlistEntry[]).map((w) => w.sticker_code))
      );
      setPartnerWishlist(
        new Set(((partnerWlRes.data ?? []) as WishlistEntry[]).map((w) => w.sticker_code))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [trade, user.id]);

  const canGiveCodes = useMemo(() => {
    return Object.entries(myCounts)
      .filter(([code, c]) => c >= 2 && (partnerCounts[code] ?? 0) === 0)
      .map(([code]) => code)
      .sort();
  }, [myCounts, partnerCounts]);

  const canReceiveCodes = useMemo(() => {
    return Object.entries(partnerCounts)
      .filter(([code, c]) => c >= 2 && (myCounts[code] ?? 0) === 0)
      .map(([code]) => code)
      .sort();
  }, [myCounts, partnerCounts]);

  const togglePick = (code: string) => {
    setMySelection((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const toggleAsk = (code: string) => {
    setPartnerSelection((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleSaveSelection = useCallback(async () => {
    if (!trade) return;
    setSubmitting(true);
    setError(null);
    // O `set_selection` valida quem é o caller. Cada lado define sua própria
    // lista do que dá; do outro lado é só um "carrinho de pedidos" local que
    // será proposto na confirmação.
    const myCodes = mySelection;
    const { error: rpcErr } = await supabase.rpc('set_selection', {
      p_trade_id: trade.id,
      p_codes: myCodes,
    });
    setSubmitting(false);
    if (rpcErr) {
      setError('Não foi possível salvar a seleção. Tente novamente.');
    }
  }, [trade, mySelection]);

  const handleConfirm = useCallback(async () => {
    if (!trade) return;
    setSubmitting(true);
    setError(null);
    const { error: rpcErr } = await supabase.rpc('confirm_trade', { p_trade_id: trade.id });
    setSubmitting(false);
    if (rpcErr) {
      setError('Não foi possível confirmar. Tente novamente.');
    } else {
      setConfirmedLocal(true);
    }
  }, [trade]);

  const handleCancel = useCallback(async () => {
    if (!trade) return;
    setSubmitting(true);
    const { error: rpcErr } = await supabase.rpc('cancel_trade', { p_trade_id: trade.id });
    setSubmitting(false);
    if (rpcErr) {
      setError('Não foi possível cancelar. Tente novamente.');
      return;
    }
    navigate('/album?tab=trocas');
  }, [trade, navigate]);

  const handleUndo = useCallback(async () => {
    if (!trade) return;
    setSubmitting(true);
    const { error: rpcErr } = await supabase.rpc('undo_trade', { p_trade_id: trade.id });
    setSubmitting(false);
    if (rpcErr) {
      setError('Não foi possível desfazer. A janela pode ter expirado.');
    } else {
      setToast('Troca desfeita. Suas figurinhas foram restauradas.');
    }
  }, [trade]);

  if (error) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-ink-300 p-8 max-w-sm w-full text-center shadow-md">
          <XCircle size={40} className="mx-auto text-danger mb-3" strokeWidth={1.75} />
          <p className="font-display font-semibold text-h3 text-ink-900 mb-2">{error}</p>
          <Button variant="primary" size="md" onClick={() => navigate('/album?tab=trocas')}>
            Voltar pra Trocas
          </Button>
        </div>
      </div>
    );
  }

  if (!trade || !partner) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="text-center text-ink-700">
          <Loader2 size={32} className="animate-spin mx-auto mb-3 text-green-700" strokeWidth={2.25} />
          <p className="font-body text-small">Carregando troca...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper">
      <header className="bg-green-700 text-white sticky top-0 z-40 border-b border-white/10">
        <div className="max-w-shell mx-auto px-4 md:px-6 py-3.5 flex items-center gap-3">
          <button
            type="button"
            aria-label="Voltar"
            onClick={() => navigate('/album?tab=trocas')}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border-[1.5px] border-white/40 text-white hover:bg-white/10"
          >
            <ArrowLeft size={16} strokeWidth={2.25} />
          </button>
          <Avatar name={partner.display_name} size="sm" />
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-semibold text-white text-base truncate">
              {partner.display_name}
            </h1>
            {partner.username && (
              <p className="text-caption text-white/70 truncate">@{partner.username}</p>
            )}
          </div>
          {reconnecting && (
            <span className="inline-flex items-center gap-1 text-caption text-white/70">
              <Loader2 size={12} className="animate-spin" /> Reconectando...
            </span>
          )}
        </div>
      </header>

      <main className="max-w-shell mx-auto px-4 md:px-6 pt-6 pb-24">
        {isExpired && (
          <ExpiredView onBack={() => navigate('/album?tab=trocas')} />
        )}

        {!isExpired && (trade.status === 'matching' || trade.status === 'selecting') && (
          <SelectionView
            mySide={mySide!}
            canGiveCodes={canGiveCodes}
            canReceiveCodes={canReceiveCodes}
            partnerCounts={partnerCounts}
            myWishlist={myWishlist}
            partnerWishlist={partnerWishlist}
            mySelection={mySelection}
            partnerSelection={partnerSelection}
            partnerSelectionFromServer={
              isUserA ? trade.selections_b : trade.selections_a
            }
            partnerName={partner.display_name}
            onTogglePick={togglePick}
            onToggleAsk={toggleAsk}
            onSave={handleSaveSelection}
            onCancel={handleCancel}
            submitting={submitting}
          />
        )}

        {!isExpired && trade.status === 'pending_confirm' && (
          <PendingConfirmView
            mySide={mySide!}
            myGives={isUserA ? trade.selections_a : trade.selections_b}
            myReceives={isUserA ? trade.selections_b : trade.selections_a}
            myCounts={myCounts}
            partnerName={partner.display_name}
            confirmedByMe={
              (isUserA && trade.confirmed_a) ||
              (!isUserA && trade.confirmed_b) ||
              confirmedLocal
            }
            partnerConfirmed={isUserA ? trade.confirmed_b : trade.confirmed_a}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            submitting={submitting}
          />
        )}

        {trade.status === 'confirmed' && (
          <ConfirmedView
            myGives={isUserA ? trade.selections_a : trade.selections_b}
            myReceives={isUserA ? trade.selections_b : trade.selections_a}
            confirmedAt={trade.confirmed_at}
            now={now}
            onUndo={handleUndo}
            onClose={() => navigate('/album?tab=trocas')}
            submitting={submitting}
          />
        )}

        {trade.status === 'cancelled' && (
          <CancelledView
            cancelledByMe={trade.cancelled_by === user.id}
            partnerName={partner.display_name}
            onBack={() => navigate('/album?tab=trocas')}
          />
        )}

        {trade.status === 'undone' && (
          <UndoneView onBack={() => navigate('/album?tab=trocas')} />
        )}
      </main>

      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subviews
// ---------------------------------------------------------------------------

interface SelectionViewProps {
  mySide: 'a' | 'b';
  canGiveCodes: string[];
  canReceiveCodes: string[];
  partnerCounts: Record<string, number>;
  myWishlist: Set<string>;
  partnerWishlist: Set<string>;
  mySelection: string[];
  partnerSelection: string[];
  partnerSelectionFromServer: string[];
  partnerName: string;
  onTogglePick: (code: string) => void;
  onToggleAsk: (code: string) => void;
  onSave: () => void;
  onCancel: () => void;
  submitting: boolean;
}

function SelectionView(props: SelectionViewProps) {
  const {
    canGiveCodes,
    canReceiveCodes,
    partnerCounts,
    myWishlist,
    partnerWishlist,
    mySelection,
    partnerSelectionFromServer,
    partnerName,
    onTogglePick,
    onSave,
    onCancel,
    submitting,
  } = props;

  const partnerHasSubmitted = partnerSelectionFromServer.length > 0;

  return (
    <div className="space-y-5">
      <section>
        <p className="bt-eyebrow text-green-700 mb-1">Troca presencial</p>
        <h2 className="font-display font-bold text-h2 text-ink-900 tracking-[-0.01em]">
          Combine a troca com {partnerName}
        </h2>
        <p className="text-small text-ink-700 mt-1">
          Marque o que você vai dar. Quando os dois selecionarem, vocês confirmam juntos
          e a baixa é feita automaticamente.
        </p>
      </section>

      <section className="bg-white border border-ink-300 rounded-xl overflow-hidden">
        <div className="px-4 pt-4 pb-3 border-b border-ink-100 flex items-center gap-2">
          <ArrowUpFromLine size={16} className="text-green-700" strokeWidth={2.5} />
          <h3 className="font-display font-semibold text-h3 text-ink-900">Eu posso dar</h3>
          <span className="ml-auto text-caption text-ink-500">
            {mySelection.length} marcada(s)
          </span>
        </div>
        <div className="p-3">
          {canGiveCodes.length === 0 ? (
            <p className="text-small text-ink-500 italic px-1 py-3">
              Você não tem repetidas que {partnerName} precise.
            </p>
          ) : (
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {canGiveCodes.map((code) => {
                const isPicked = mySelection.includes(code);
                const star = partnerWishlist.has(code);
                return (
                  <li key={code}>
                    <button
                      type="button"
                      onClick={() => onTogglePick(code)}
                      className={[
                        'w-full text-left px-3 py-2 rounded-md border-[1.5px] btn-focus transition-colors',
                        isPicked
                          ? 'bg-green-100 border-green-700'
                          : 'bg-white border-ink-300 hover:border-green-700',
                      ].join(' ')}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          aria-hidden
                          className={[
                            'w-4 h-4 rounded-sm border-[1.5px] inline-flex items-center justify-center shrink-0',
                            isPicked ? 'bg-green-700 border-green-700' : 'border-ink-300',
                          ].join(' ')}
                        >
                          {isPicked && (
                            <Check size={12} strokeWidth={3} className="text-white" />
                          )}
                        </span>
                        <span className="font-mono font-bold text-[13px] text-ink-900">
                          {code}
                        </span>
                        {star && (
                          <span
                            aria-label="prioridade do parceiro"
                            className="ml-auto bg-gold-500 text-ink-900 px-1 rounded-sm text-[9px] font-bold leading-none h-3.5 inline-flex items-center"
                          >
                            ★
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <section className="bg-white border border-ink-300 rounded-xl overflow-hidden">
        <div className="px-4 pt-4 pb-3 border-b border-ink-100 flex items-center gap-2">
          <ArrowDownToLine size={16} className="text-green-700" strokeWidth={2.5} />
          <h3 className="font-display font-semibold text-h3 text-ink-900">
            {partnerName} pode me dar
          </h3>
        </div>
        <div className="p-3">
          {canReceiveCodes.length === 0 ? (
            <p className="text-small text-ink-500 italic px-1 py-3">
              {partnerName} não tem repetidas que você precise agora.
            </p>
          ) : (
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {canReceiveCodes.map((code) => {
                const partnerCount = partnerCounts[code] ?? 0;
                const onlyOne = partnerCount <= 1;
                const star = myWishlist.has(code);
                return (
                  <li key={code}>
                    <div
                      className={[
                        'w-full px-3 py-2 rounded-md border-[1.5px] bg-ink-100 border-ink-300',
                        onlyOne ? 'opacity-60' : '',
                      ].join(' ')}
                      title={onlyOne ? 'Essa é a única que ele tem' : undefined}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-[13px] text-ink-900">
                          {code}
                        </span>
                        {star && (
                          <span
                            aria-label="está na sua wishlist"
                            className="ml-auto bg-gold-500 text-ink-900 px-1 rounded-sm text-[9px] font-bold leading-none h-3.5 inline-flex items-center"
                          >
                            ★
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="text-caption text-ink-500 px-1 mt-2">
            Quem decide o que dar é o dono. Marque suas figurinhas no card de cima e peça
            pra {partnerName} fazer o mesmo.
          </p>
        </div>
      </section>

      {!partnerHasSubmitted && (
        <p className="text-small text-ink-500 inline-flex items-center gap-1.5">
          <Loader2 size={14} className="animate-spin" /> Aguardando seleção do parceiro...
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="primary"
          size="md"
          fullWidth
          onClick={onSave}
          loading={submitting}
        >
          Salvar minha seleção
        </Button>
        <Button variant="ghost" size="md" fullWidth onClick={onCancel} disabled={submitting}>
          Cancelar troca
        </Button>
      </div>
    </div>
  );
}

interface PendingConfirmViewProps {
  mySide: 'a' | 'b';
  myGives: string[];
  myReceives: string[];
  myCounts: Record<string, number>;
  partnerName: string;
  confirmedByMe: boolean;
  partnerConfirmed: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  submitting: boolean;
}

function PendingConfirmView(props: PendingConfirmViewProps) {
  const {
    myGives,
    myReceives,
    myCounts,
    partnerName,
    confirmedByMe,
    partnerConfirmed,
    onConfirm,
    onCancel,
    submitting,
  } = props;

  return (
    <div className="space-y-5">
      <section>
        <p className="bt-eyebrow text-green-700 mb-1">Confirmação</p>
        <h2 className="font-display font-bold text-h2 text-ink-900 tracking-[-0.01em]">
          Confere o que vocês combinaram
        </h2>
        <p className="text-small text-ink-700 mt-1">
          Os dois precisam tocar em "Vamos Trocar!" pra a baixa acontecer.
        </p>
      </section>

      <section className="bg-white border border-ink-300 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <ArrowUpFromLine size={16} className="text-green-700" strokeWidth={2.5} />
          <h3 className="font-display font-semibold text-h3 text-ink-900">
            Eu dou: {myGives.length} figurinha(s)
          </h3>
        </div>
        {myGives.length === 0 ? (
          <p className="text-small text-ink-500 italic">Nada da sua parte.</p>
        ) : (
          <ul className="space-y-1.5">
            {myGives.map((code) => {
              const have = myCounts[code] ?? 0;
              const after = Math.max(0, have - 1);
              let line: string;
              if (have === 2) line = 'deixará de ser repetida.';
              else if (have >= 3) line = `você ficará com ${after}.`;
              else line = `você ficará com ${after}.`;
              return (
                <li key={code} className="text-small text-ink-700 flex items-baseline gap-2">
                  <span className="font-mono font-bold text-ink-900">{code}</span>
                  <span className="text-ink-500">{line}</span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="bg-white border border-ink-300 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <ArrowDownToLine size={16} className="text-green-700" strokeWidth={2.5} />
          <h3 className="font-display font-semibold text-h3 text-ink-900">
            Eu recebo: {myReceives.length} figurinha(s)
          </h3>
        </div>
        {myReceives.length === 0 ? (
          <p className="text-small text-ink-500 italic">Nada do parceiro.</p>
        ) : (
          <ul className="space-y-1.5">
            {myReceives.map((code) => {
              const have = myCounts[code] ?? 0;
              const line = have === 0 ? 'será sua nova.' : `você ficará com ${have + 1}.`;
              return (
                <li key={code} className="text-small text-ink-700 flex items-baseline gap-2">
                  <span className="font-mono font-bold text-ink-900">{code}</span>
                  <span className="text-ink-500">{line}</span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {confirmedByMe && (
        <p className="text-small text-green-700 inline-flex items-center gap-1.5">
          <CheckCircle2 size={14} strokeWidth={2.5} /> Você já confirmou.
          {!partnerConfirmed && (
            <span className="text-ink-500"> Aguardando {partnerName}...</span>
          )}
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="primary"
          size="md"
          fullWidth
          onClick={onConfirm}
          disabled={confirmedByMe}
          loading={submitting && !confirmedByMe}
        >
          Vamos Trocar!
        </Button>
        <Button
          variant="ghost"
          size="md"
          fullWidth
          onClick={onCancel}
          disabled={submitting}
        >
          Não Vamos Trocar...
        </Button>
      </div>
    </div>
  );
}

interface ConfirmedViewProps {
  myGives: string[];
  myReceives: string[];
  confirmedAt: string | null;
  now: number;
  onUndo: () => void;
  onClose: () => void;
  submitting: boolean;
}

function ConfirmedView(props: ConfirmedViewProps) {
  const { myGives, myReceives, confirmedAt, now, onUndo, onClose, submitting } = props;

  const confirmedMs = confirmedAt ? new Date(confirmedAt).getTime() : 0;
  const remainingMs = Math.max(0, confirmedMs + UNDO_MS - now);
  const canUndo = remainingMs > 0;
  const mm = Math.floor(remainingMs / 60000);
  const ss = Math.floor((remainingMs % 60000) / 1000)
    .toString()
    .padStart(2, '0');

  return (
    <div className="space-y-5">
      <section className="bg-green-100 border border-green-500/40 rounded-2xl p-5 text-center">
        <Sparkles size={36} className="mx-auto text-green-700 mb-2" strokeWidth={1.75} />
        <h2 className="font-display font-bold text-h2 text-green-900 tracking-[-0.01em]">
          Troca realizada com sucesso!
        </h2>
        <p className="text-small text-green-900/80 mt-1">
          Suas figurinhas já foram atualizadas no álbum.
        </p>
      </section>

      <section className="bg-white border border-ink-300 rounded-xl p-4 space-y-3">
        <div>
          <h3 className="font-display font-semibold text-h3 text-ink-900 mb-1">
            Você deu
          </h3>
          {myGives.length === 0 ? (
            <p className="text-small text-ink-500 italic">Nada.</p>
          ) : (
            <p className="font-mono text-small text-ink-700 break-all">{myGives.join(', ')}</p>
          )}
        </div>
        <div>
          <h3 className="font-display font-semibold text-h3 text-ink-900 mb-1">
            Você recebeu
          </h3>
          {myReceives.length === 0 ? (
            <p className="text-small text-ink-500 italic">Nada.</p>
          ) : (
            <p className="font-mono text-small text-ink-700 break-all">
              {myReceives.join(', ')}
            </p>
          )}
        </div>
      </section>

      {canUndo && (
        <div className="bg-gold-100 border border-gold-500/50 rounded-xl p-4 flex items-center gap-3">
          <RotateCcw size={20} className="text-gold-600 shrink-0" strokeWidth={2.25} />
          <div className="flex-1">
            <p className="font-display font-semibold text-h3 text-ink-900">
              Algo errado? Desfaça em {mm}:{ss}.
            </p>
            <p className="text-caption text-ink-500 mt-0.5">
              Reverte os contadores e remove o evento do feed.
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={onUndo}
            loading={submitting}
          >
            Desfazer
          </Button>
        </div>
      )}

      <Button variant="primary" size="md" fullWidth onClick={onClose}>
        Voltar pra Trocas
      </Button>
    </div>
  );
}

interface CancelledViewProps {
  cancelledByMe: boolean;
  partnerName: string;
  onBack: () => void;
}

function CancelledView({ cancelledByMe, partnerName, onBack }: CancelledViewProps) {
  return (
    <div className="bg-white border border-ink-300 rounded-2xl p-6 text-center max-w-sm mx-auto">
      <XCircle size={36} className="mx-auto text-ink-500 mb-2" strokeWidth={1.75} />
      <h2 className="font-display font-semibold text-h3 text-ink-900 mb-1">
        A troca foi cancelada
      </h2>
      <p className="text-small text-ink-700 mb-5">
        {cancelledByMe
          ? 'Você cancelou a troca.'
          : `${partnerName} não quis trocar agora.`}
      </p>
      <Button variant="primary" size="md" fullWidth onClick={onBack}>
        Voltar pra Trocas
      </Button>
    </div>
  );
}

function ExpiredView({ onBack }: { onBack: () => void }) {
  return (
    <div className="bg-white border border-ink-300 rounded-2xl p-6 text-center max-w-sm mx-auto">
      <XCircle size={36} className="mx-auto text-ink-500 mb-2" strokeWidth={1.75} />
      <h2 className="font-display font-semibold text-h3 text-ink-900 mb-1">A troca expirou</h2>
      <p className="text-small text-ink-700 mb-5">
        Trocas têm validade de uma hora. Comece uma nova quando estiver com a pessoa.
      </p>
      <Button variant="primary" size="md" fullWidth onClick={onBack}>
        Voltar pra Trocas
      </Button>
    </div>
  );
}

function UndoneView({ onBack }: { onBack: () => void }) {
  return (
    <div className="bg-white border border-ink-300 rounded-2xl p-6 text-center max-w-sm mx-auto">
      <RotateCcw size={36} className="mx-auto text-ink-500 mb-2" strokeWidth={1.75} />
      <h2 className="font-display font-semibold text-h3 text-ink-900 mb-1">Troca desfeita</h2>
      <p className="text-small text-ink-700 mb-5">Suas figurinhas foram restauradas.</p>
      <Button variant="primary" size="md" fullWidth onClick={onBack}>
        Voltar pra Trocas
      </Button>
    </div>
  );
}
